import React from 'react';
import {
    DESIGN_MODULE_CATALOG,
    DESIGN_MODULE_SECTIONS,
    FLOW_MODE_OPTIONS,
    FLOW_MODULE_IDS,
    PNC_CATALOG_MODULE_IDS,
    buildLayoutFromDesignSettings,
    getFlowMode,
    normalizeDesignSettings,
    updateDesignModule
} from '../utils';

const splitList = (value) => (value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const joinList = (value) => Array.isArray(value) ? value.join(', ') : '';

function Field({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
            {children}
        </label>
    );
}

function ModuleCard({ module, config, onChange, features = [] }) {
    const handleLogoUpload = (file) => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const logoOption = {
                id: `uploaded-${Date.now()}`,
                name: file.name,
                url: reader.result
            };
            onChange({
                logoId: logoOption.id,
                logoName: logoOption.name,
                logoUrl: logoOption.url,
                logoOptions: [...(config.logoOptions || []), logoOption]
            });
        };
        reader.readAsDataURL(file);
    };

    return (
        <div style={{
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '0.85rem',
            background: config.enabled ? 'var(--bg-primary)' : 'var(--bg-secondary)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.65rem',
            opacity: config.enabled ? 1 : 0.78
        }}>
            {FLOW_MODULE_IDS.includes(module.id) ? (
                <strong style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{module.label} options</strong>
            ) : (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={Boolean(config.enabled)}
                        onChange={(e) => onChange({ enabled: e.target.checked })}
                    />
                    <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{module.label}</strong>
                </label>
            )}
            {module.id === 'intro' && (
                <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-secondary)', lineHeight: 1.4, textTransform: 'none', letterSpacing: 0 }}>
                    Shown as a front page. Visitors use the start button to enter the configurator.
                </p>
            )}

            {config.enabled && (
                <>
                    {module.variants && (
                        <Field label={module.id === 'logo' ? 'Placement' : 'Variant'}>
                            <select
                                className="input-field"
                                value={module.id === 'logo' ? config.placement : config.variant}
                                onChange={(e) => onChange(module.id === 'logo' ? { placement: e.target.value } : { variant: e.target.value })}
                                style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }}
                            >
                                {module.variants.map(variant => (
                                    <option key={variant.value} value={variant.value}>{variant.label}</option>
                                ))}
                            </select>
                        </Field>
                    )}

                    {module.id === 'imageCarousel' && (
                        <Field label="Bound feature">
                            <select
                                className="input-field"
                                value={config.bindFeature || ''}
                                onChange={(e) => onChange({ bindFeature: e.target.value })}
                                style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }}
                            >
                                <option value="">Auto (carousel / system feature)</option>
                                {features.map(feature => (
                                    <option key={feature.id || feature.name} value={feature.name}>{feature.displayText || feature.name}</option>
                                ))}
                            </select>
                        </Field>
                    )}

                    {module.id === 'logo' && (
                        <>
                            <Field label="Organization logo">
                                <select
                                    className="input-field"
                                    value={config.logoId || ''}
                                    onChange={(e) => {
                                        const selected = (config.logoOptions || []).find(option => option.id === e.target.value);
                                        onChange({
                                            logoId: selected?.id || '',
                                            logoName: selected?.name || '',
                                            logoUrl: selected?.url || ''
                                        });
                                    }}
                                    style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }}
                                >
                                    <option value="">Use text placeholder</option>
                                    {(config.logoOptions || []).map(option => (
                                        <option key={option.id} value={option.id}>{option.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                                <i className="fa-solid fa-upload"></i>
                                Upload logo
                                <input
                                    type="file"
                                    accept="image/svg+xml,image/png,image/webp"
                                    onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </>
                    )}

                    {'title' in config && (
                        <Field label="Title">
                            <input className="input-field" value={config.title || ''} onChange={(e) => onChange({ title: e.target.value })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'text' in config && (
                        <Field label="Text">
                            <textarea className="textarea-field" value={config.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ margin: 0, padding: '0.45rem', minHeight: '64px', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'placeholder' in config && (
                        <Field label="Placeholder">
                            <input className="input-field" value={config.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'buttonText' in config && (
                        <Field label={module.id === 'intro' ? 'Start button text' : 'Button text'}>
                            <input className="input-field" value={config.buttonText || ''} onChange={(e) => onChange({ buttonText: e.target.value })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'infoText' in config && (
                        <Field label="Info text">
                            <textarea className="textarea-field" value={config.infoText || ''} onChange={(e) => onChange({ infoText: e.target.value })} style={{ margin: 0, padding: '0.45rem', minHeight: '64px', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'label' in config && (
                        <Field label="Label">
                            <input className="input-field" value={config.label || ''} onChange={(e) => onChange({ label: e.target.value })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'items' in config && !FLOW_MODULE_IDS.includes(module.id) && (
                        <Field label="Items">
                            <input className="input-field" value={joinList(config.items)} onChange={(e) => onChange({ items: splitList(e.target.value) })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                    {'steps' in config && !FLOW_MODULE_IDS.includes(module.id) && (
                        <Field label="Steps">
                            <input className="input-field" value={joinList(config.steps)} onChange={(e) => onChange({ steps: splitList(e.target.value) })} style={{ margin: 0, padding: '0.45rem', fontSize: '0.8rem' }} />
                        </Field>
                    )}
                </>
            )}
        </div>
    );
}

export default function ModuleSettingsPanel({ properties, setProperties, features = [] }) {
    const settings = normalizeDesignSettings(properties.designSettings);
    const pncCatalogModule = DESIGN_MODULE_CATALOG.find(module => module.id === 'pncCatalog');
    const pncChildModules = DESIGN_MODULE_CATALOG.filter(module => PNC_CATALOG_MODULE_IDS.includes(module.id));

    const updateModule = (moduleId, patch) => {
        const nextSettings = {
            ...updateDesignModule(settings, moduleId, patch),
            configured: true
        };
        setProperties(prev => ({
            ...prev,
            designSettings: nextSettings,
            layoutMapping: buildLayoutFromDesignSettings(nextSettings)
        }));
    };

    const renderModuleCard = (module) => (
        <ModuleCard
            key={module.id}
            module={module}
            config={settings.modules[module.id]}
            onChange={(patch) => updateModule(module.id, patch)}
            features={features}
        />
    );

    const flowMode = getFlowMode(settings);
    const selectedFlowModule = DESIGN_MODULE_CATALOG.find(module => module.id === flowMode);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {DESIGN_MODULE_SECTIONS.map(section => {
                const sectionModules = DESIGN_MODULE_CATALOG.filter(module => module.section === section.id);
                const regularModules = sectionModules.filter(module => (
                    module.id !== 'pncCatalog'
                    && !PNC_CATALOG_MODULE_IDS.includes(module.id)
                    && !FLOW_MODULE_IDS.includes(module.id)
                ));

                return (
                    <section key={section.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        <h5 style={{ margin: 0, fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '0.03em' }}>
                            {section.label}
                        </h5>
                        {section.id === 'middle' ? (
                            <div style={{
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                padding: '0.85rem',
                                background: 'var(--bg-primary)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem'
                            }}>
                                {FLOW_MODE_OPTIONS.map(option => (
                                    <label key={option.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.55rem', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="flow-mode"
                                            checked={flowMode === option.id}
                                            onChange={() => updateModule(option.id, { enabled: true })}
                                            style={{ marginTop: '0.2rem' }}
                                        />
                                        <span>
                                            <strong style={{ display: 'block', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{option.label}</strong>
                                            <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{option.description}</span>
                                        </span>
                                    </label>
                                ))}
                                {selectedFlowModule && (
                                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                                        <ModuleCard
                                            module={selectedFlowModule}
                                            config={{ ...settings.modules[selectedFlowModule.id], enabled: true }}
                                            onChange={(patch) => updateModule(selectedFlowModule.id, { ...patch, enabled: true })}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : regularModules.map(renderModuleCard)}
                        {section.id === 'start' && pncCatalogModule && (
                            <>
                                {renderModuleCard(pncCatalogModule)}
                                {settings.modules.pncCatalog?.enabled && (
                                    <div style={{
                                        border: '1px solid var(--border)',
                                        borderRadius: '8px',
                                        padding: '0.65rem',
                                        background: 'var(--bg-primary)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.65rem'
                                    }}>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.35 }}>
                                            PNC Search, product type cards, Filter, and Compare are available when this catalog is enabled.
                                        </div>
                                        {pncChildModules.map(renderModuleCard)}
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                );
            })}

            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                Use Structure to move selected modules between layout zones.
            </div>
        </div>
    );
}
